import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

const AboutUs = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="bg-gradient-to-b from-[#FFF7F2] to-[#FEF8ED] min-h-screen">
     
      {/* Hero Section */}
      <div className="relative h-[60vh] w-full overflow-hidden">
        <motion.img
          src="/images/about us img.png"
          alt="Modern library space"
          className="w-full h-full object-cover object-center"
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
        />
        <div className="absolute inset-0  flex items-center justify-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-4xl md:text-6xl font-extrabold text-white text-center drop-shadow-lg"
          >
            Revolutionizing Reading Books
          </motion.h1>
        </div>
      </div>

      {/* Content Sections */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-16">


        {/* About Introduction */}
        <section className="glass-card bg-white/90 backdrop-blur-lg rounded-3xl p-8 md:p-12 shadow-2xl shadow-orange-100 border border-white/20">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8 text-center">
              <h2 className="text-gradient bg-clip-text text-transparent bg-gradient-to-r from-[#FF7236] to-[#EB5E22] text-3xl md:text-4xl font-bold mb-6">
                About Books At Doorstep
              </h2>
              <p className="text-md md:text-xl text-gray-800 leading-relaxed">
                <strong>Books At Doorstep</strong> is an initiative by Applause
                E-Commerce Business Solutions. It was launched with a vision to
                democratize reading by removing barriers like travel, access, and
                cost. We believe knowledge should be as easy to access as food
                delivery – and that’s what we’re making possible for book lovers
                across Pune. Our dedicated team curates and delivers books tailored
                to each reader’s preferences, so they can focus on what matters
                most—reading.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <FeatureCard
                icon="💰"
                title="Echo Credits"
                text="Earn and use Echo Credits for discounts and special offers"
              />
              <FeatureCard
                icon="🔒"
                title="Flexible Subscriptions"
                text="Choose from a variety of subscription plans that suit your reading habits"
              />
              <FeatureCard
                icon="♻️"
                title="Eco-Conscious Model"
                text="Each book serves 10+ readers, reducing paper waste by 75%"
              />
              <FeatureCard
                icon="💡"
                title="Interactive Reading"
                text="Dive into a next-gen reading experience with our titles."
              />
            </div>
          </div>
          </section>

                {/* Problem & Solution */}
                <motion.section
          ref={ref}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={fadeIn}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-1 md:grid-cols-2 gap-8"
        >
          <div className="bg-white/90 backdrop-blur-lg rounded-3xl p-8 shadow-lg border border-white/20">
            <h2 className="text-2xl md:text-3xl font-bold text-[#EB5E22] mb-4">
              🔹 The Problem
            </h2>
            <p className="text-gray-700 leading-relaxed">
              In today’s fast-paced digital world, people have less time and fewer
              opportunities to visit libraries or bookstores. Physical access,
              time constraints, and rising book prices are pushing readers away
              from books. Additionally, the lack of customized reading experiences
              often results in people losing interest in reading altogether.
            </p>
          </div>
          <div className="bg-white/90 backdrop-blur-lg rounded-3xl p-8 shadow-lg border border-white/20">
            <h2 className="text-2xl md:text-3xl font-bold text-[#EB5E22] mb-4">
              🔹 The Solution
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Books At Doorstep solves this problem by offering a personalized,
              cost-effective, and time-saving solution. We bring the library to your
              home with free delivery, a vast collection, and flexible rental plans.
              Whether you want to read a bestseller, a regional language novel, or
              academic content—we’ve got you covered.
            </p>
          </div>
        </motion.section>

        {/* Why This Initiative */}
        <motion.section
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={fadeIn}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="flex flex-col lg:flex-row gap-8 items-center glass-card bg-[#FF7236]/10 p-8 rounded-3xl border border-white/20"
        >
          <div className="lg:w-1/2">
            <img
              src="/images/book-flip.png"
              alt="Sustainable reading concept"
              className="rounded-2xl shadow-xl hover:scale-105 transition-transform duration-500"
            />
          </div>
          <div className="lg:w-1/2 space-y-6">
            <h3 className="text-3xl font-bold bg-gradient-to-r from-[#FF7236] to-[#EB5E22] bg-clip-text text-transparent">
              Why This Initiative
            </h3>
            <p className="text-md text-gray-800 leading-relaxed">
              Books at Doorstep aims to raise awareness about sustainable
              paper use and give new life to pre-loved books. Instead of letting
              books gather dust or end up in landfills, we circulate them among
              readers across Pune, extending each book’s lifecycle and reducing
              waste.
            </p>
            <ul className="space-y-4">
              <li className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#FF7236] flex items-center justify-center text-white">
                  ✓
                </div>
                <span className="text-gray-800">
                  Carbon-neutral delivery fleet
                </span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#FF7236] flex items-center justify-center text-white">
                  ✓
                </div>
                <span className="text-gray-800">
                  Curated picks matched to your interests
                </span>
              </li>
              <li className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#FF7236] flex items-center justify-center text-white">
                  ✓
                </div>
                <span className="text-gray-800">
                  Flexible rental and buy options
                </span>
              </li>
            </ul>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

const FeatureCard = ({ icon, title, text }) => (
  <div className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 border border-orange-50">
    <div className="text-4xl mb-4">{icon}</div>
    <h3 className="text-xl font-semibold text-gray-800 mb-2">{title}</h3>
    <p className="text-gray-600 leading-tight">{text}</p>
  </div>
);

export default AboutUs;
